import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';

export default function Univers() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.reveal-item');
            elements.forEach((el, i) => {
              setTimeout(() => { el.classList.add('visible'); }, i * 120);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative w-full h-[55vh] min-h-[380px] overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20spa%20entrance%20with%20soft%20warm%20lighting%2C%20minimalist%20luxury%20interior%2C%20natural%20wood%20and%20stone%20elements%2C%20potted%20plants%2C%20peaceful%20atmosphere%2C%20nude%20beige%20and%20cream%20tones%2C%20professional%20architectural%20photography&width=1600&height=700&seq=kds-univers-hero&orientation=landscape"
            alt="L'Univers KD'S BEAUTY"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/50" />
        </div>
        <div className="relative z-10 text-center px-4 w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 mb-5">
            <span className="text-background-50/90 text-xs tracking-[0.15em] uppercase font-label">À propos</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 font-light mb-4">L'Univers KD'S</h1>
          <p className="text-background-50/70 text-base md:text-lg max-w-2xl mx-auto">Découvrez l'histoire, la philosophie et les femmes qui font vivre votre spa</p>
        </div>
      </section>

      {/* Notre Histoire */}
      <section ref={sectionRef} className="w-full py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-6xl mx-auto">
            <div className="reveal-item opacity-0 translate-y-6 transition-all duration-700 lg:w-1/2" style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
              <span className="inline-block px-4 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 tracking-wider uppercase">Notre Histoire</span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-800 mb-5">Du service hospitalier au temple de la beauté</h2>

              <p className="text-foreground-600 leading-relaxed mb-4">
                Il y a des carrières que l'on choisit par vocation, et d'autres par passion qui finissent par tout emporter. Madame Ahui Brigitte a d'abord répondu à l'appel du soin en devenant responsable au sein d'un hôpital. Un métier noble, exigeant, où l'humain est au cœur de chaque geste.
              </p>

              <p className="text-foreground-600 leading-relaxed mb-4">
                Pourtant, une autre flamme brûlait en elle, plus forte encore que le devoir : celle de l'esthétique et de la beauté. Là où certains ne voient que des apparences, elle percevait l'essentiel — la confiance retrouvée, le sourire qui s'illumine devant le miroir, cette étincelle dans le regard d'une femme qui se sent belle.
              </p>

              <p className="text-foreground-600 leading-relaxed mb-4">
                Alors elle a osé. Elle a quitté ses fonctions et la sécurité d'une carrière toute tracée pour se lancer dans l'inconnu, guidée par une conviction inébranlable : la beauté est un soin à part entière, un droit que chaque femme mérite de s'offrir.
              </p>

              <p className="text-foreground-600 leading-relaxed mb-4">
                Après des années de formation intensive en esthétique et cosmétologie, elle a posé ses valises au cœur d'Angré Château et a créé KD'S BEAUTY. Non pas un simple institut, mais un écrin pensé comme un refuge. Un lieu où l'expertise médicale héritée de son passé hospitalier dialogue avec la douceur des soins de beauté.
              </p>

              <p className="text-foreground-600 leading-relaxed">
                Aujourd'hui, chaque cliente qui franchit nos portes vit cette promesse : celle d'un soin aussi rigoureux que bienveillant, porté par une femme qui a tout quitté pour voir rayonner les autres.
              </p>
            </div>
            <div className="reveal-item opacity-0 translate-y-6 transition-all duration-700 lg:w-1/2" style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', transitionDelay: '150ms' }}>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_8a9ab67bf10bd6660b0441c9dc9a6848_95722c27.jpg"
                  alt="Intérieur du salon KD'S BEAUTY"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-background-50/90 backdrop-blur-sm rounded-xl px-4 py-3 border border-background-200/50">
                    <p className="text-sm text-foreground-700 font-medium">KD'S BEAUTY — Angré Château</p>
                    <p className="text-xs text-foreground-500">Là où la passion a trouvé son écrin</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fondatrice */}
      <section className="w-full py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-6xl mx-auto">
            <div className="reveal-item opacity-0 translate-y-6 transition-all duration-700 lg:w-1/2 order-2 lg:order-1" style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Confident%20elegant%20African%20woman%20spa%20founder%20in%20white%20blouse%2C%20warm%20golden%20ambient%20lighting%2C%20luxury%20spa%20interior%20background%2C%20professional%20portrait%20photography%2C%20sophisticated%20and%20feminine%2C%20beige%20and%20gold%20tones%2C%20soft%20focus%20background&width=600&height=750&seq=kds-founder-01&orientation=portrait"
                  alt="Mme Ahui Brigitte, fondatrice KD'S BEAUTY"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
            <div className="reveal-item opacity-0 translate-y-6 transition-all duration-700 lg:w-1/2 order-1 lg:order-2" style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', transitionDelay: '150ms' }}>
              <span className="inline-block px-4 py-1.5 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 tracking-wider uppercase">La Fondatrice</span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-800 mb-2">Mme Ahui Brigitte</h2>
              <p className="text-primary-600 text-sm tracking-wider uppercase mb-5">Esthéticienne & Fondatrice</p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Passée par le milieu hospitalier où elle a exercé en tant que responsable, Madame Ahui a toujours eu le soin de l'autre chevillé au corps. C'est cette même exigence qu'elle a transposée dans l'univers de l'esthétique, en se formant auprès des meilleurs instituts pour maîtriser les techniques les plus pointues.
              </p>
              <blockquote className="border-l-2 border-primary-400 pl-5 py-2 mb-4">
                <p className="text-foreground-700 italic leading-relaxed">
                  « J'ai quitté la blouse blanche de l'hôpital pour celle du spa, mais je n'ai jamais cessé de soigner. La beauté est une forme de médecine — celle qui guérit le regard qu'on porte sur soi-même. »
                </p>
              </blockquote>
              <p className="text-foreground-600 leading-relaxed">
                Passionnée et exigeante, elle veille personnellement à la qualité de chaque prestation et à la formation continue de son équipe. Son rêve devenu réalité porte aujourd'hui un nom : KD'S BEAUTY.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Charte Qualité */}
      <section className="w-full py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4 tracking-wider uppercase">Qualité</span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-800 mb-3">Notre Charte Qualité</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">Des engagements concrets pour votre satisfaction</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: 'ri-shield-check-line', title: 'Hygiène Irréprochable', desc: 'Protocoles stricts de désinfection et matériel stérilisé entre chaque cliente. Votre sécurité est notre priorité absolue.' },
              { icon: 'ri-flask-line', title: 'Produits d\'Exception', desc: 'Marques professionnelles sélectionnées pour leur efficacité et leur respect de la peau. Clean beauty, résultats garantis.' },
              { icon: 'ri-user-heart-line', title: 'Accueil Personnalisé', desc: 'Un diagnostic complet avant chaque soin pour adapter le protocole à vos besoins. Chaque visite est unique, comme vous.' },
            ].map((item, i) => (
              <div key={i} className="bg-background-50 rounded-2xl p-6 md:p-7 text-center border border-background-200/60 hover:border-primary-300/70 transition-all duration-500">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-50">
                  <div className="w-5 h-5 flex items-center justify-center"><i className={`${item.icon} text-xl text-secondary-500`} /></div>
                </div>
                <h3 className="font-heading text-lg text-foreground-800 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-16 bg-accent-500">
        <div className="w-full px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-background-50 mb-4">Venez vivre l'expérience</h2>
          <p className="text-background-50/80 max-w-xl mx-auto mb-8">Passez la porte de KD'S BEAUTY et laissez-vous transporter</p>
          <Link to="/rdv" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-medium bg-green-600 text-white hover:bg-green-700 transition-all duration-300">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg" />
            </div>
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </Layout>
  );
}